import { AnyAction, ThunkDispatch } from '@reduxjs/toolkit';
import * as React from 'react';

import { asyncNoop, noop } from 'bones/utils/noop';
import { createDataCatalogSlice } from 'bones/utils/store/dataCatalog/dataCatalog.slice';
import { DataCatalogState } from 'bones/utils/store/dataCatalog/types';

export type DataCatalogSlice<State, Connection> = ReturnType<
  typeof createDataCatalogSlice<State, Connection>
>;
export const DataCatalogSliceContext = React.createContext<DataCatalogSlice<
  any,
  any
> | null>(null);
export const useDataCatalogSlice = <State, Connection>() =>
  React.useContext<DataCatalogSlice<State, Connection>>(
    DataCatalogSliceContext as any
  );

export type DataCatalogGetState = () => DataCatalogState;
export const DataCatalogGetStateContext =
  React.createContext<DataCatalogGetState>(noop as any);
export const useDataCatalogGetState = () =>
  React.useContext(DataCatalogGetStateContext);

export type DataCatalogDispatch = ThunkDispatch<
  DataCatalogState,
  any,
  AnyAction
>;
export const DataCatalogDispatchContext =
  React.createContext<DataCatalogDispatch>(asyncNoop as any);
export const useDataCatalogDispatch = () =>
  React.useContext(DataCatalogDispatchContext);

export type DataCatalogSelector = <T>(
  selector: (state: DataCatalogState) => T,
  equalityFn?: ((prev: T, curr: T) => boolean) | undefined
) => T;
export const DataCatalogSelectorContext =
  React.createContext<DataCatalogSelector>(noop as any);
export const useDataCatalogSelector = () =>
  React.useContext(DataCatalogSelectorContext);

export const useDataCatalogStoreProvider =
  <State, Connection>(
    slice: DataCatalogSlice<State, Connection>
  ): React.VFC<{
    getState: DataCatalogGetState;
    dispatch: DataCatalogDispatch;
    selector: DataCatalogSelector;
    children: React.ReactNode;
  }> =>
  // eslint-disable-next-line react/display-name
  ({ getState, dispatch, selector, children }) =>
    (
      <DataCatalogSliceContext.Provider value={slice}>
        <DataCatalogGetStateContext.Provider value={getState}>
          <DataCatalogDispatchContext.Provider value={dispatch}>
            <DataCatalogSelectorContext.Provider value={selector}>
              {children}
            </DataCatalogSelectorContext.Provider>
          </DataCatalogDispatchContext.Provider>
        </DataCatalogGetStateContext.Provider>
      </DataCatalogSliceContext.Provider>
    );
